<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useLayoutStore } from "@/stores/layout";
import { useMessageStore } from "@/stores/message";
import { useRouter } from "vue-router";
import { getSelfScheduleRemind } from "@/api/user";
import { getIsOpenClassTeacher, AddTutorClientUsageLog } from "@/api/teachinfo";
import { getCookie } from "@/utils/tools";
import contactIcon from "@/assets/img/common/Group_message1.png";
import ImIframe from "@/components/im/ImIframe.vue";

const layoutStore = useLayoutStore();
const messageStore = useMessageStore();
const router = useRouter();

const showPublic = ref(false);
/** Vue2 lessonReminder: GetSelfScheduleRemind → IsShow / Tips；SignalR 可实时覆盖 */
const showClass = ref(false);
const classTips = ref("Class");

watch(
  () => messageStore.selfScheduleRemindMsg,
  (msg) => {
    if (!msg) return;
    showClass.value = !!msg.isShow;
    if (msg.tips) classTips.value = msg.tips;
  },
  { deep: true }
);

/** NEW Contact TM\CS 入口开关：VITE_SHOW_CONTACT_TM_NEW=true|false */
const showContactTmNew = computed(
  () => String(import.meta.env.VITE_SHOW_CONTACT_TM_NEW).toLowerCase() === "true"
);

/** NEW Contact TM\CS：prod → im.acadsoc.com.cn；其它 → aim.dev */
const contactTmUrl = computed(() =>
  import.meta.env.VITE_APP_MODE === "production"
    ? "https://im.acadsoc.com.cn/"
    : "https://aim.dev.acadsoc.com"
);

const floats = computed(() => [
  { key: "feedback", label: "Feedback", iconClass: "feedback-menu" },
  { key: "class", label: classTips.value || "Class", iconClass: "class-menu" },
  { key: "moodle", label: "Moodle Message", iconClass: "moodle-menu" },
  { key: "public", label: "Public Class", iconClass: "public-menu" },
]);

function isVisible(item) {
  if (item.key === "public") return showPublic.value;
  if (item.key === "class") return showClass.value;
  return true;
}

function onClick(item) {
  if (item.key === "feedback") {
    layoutStore.openFeedbackDialog("");
    return;
  }
  if (item.key === "class") {
    router.push({ name: "releaseTime" }).catch(() => {});
    return;
  }
  if (item.key === "moodle") {
    router.push("/trainingCenter/moodleMessage").catch(() => {});
    return;
  }
  if (item.key === "public") {
    router.push("/publicClass").catch(() => {});
  }
}

function openContactNew() {
  window.open(contactTmUrl.value, "_blank");
}

onMounted(() => {
  getIsOpenClassTeacher()
    .then((res) => {
      if (res.data?.ErrorCode === 0 && res.data.Body) {
        showPublic.value = !!res.data.Body;
      }
      if (getCookie("client") === "tutorclient") {
        AddTutorClientUsageLog().catch(() => {});
      }
    })
    .catch(() => {
      showPublic.value = false;
    });

  getSelfScheduleRemind()
    .then((res) => {
      const body = res?.data?.Body ?? res?.data ?? res;
      if (!body) return;
      messageStore.addSelfScheduleRemindMsg(body);
      showClass.value = !!(body.IsShow ?? body.isShow);
      classTips.value = body.Tips || body.tips || "Class";
    })
    .catch(() => {
      showClass.value = false;
    });
});
</script>

<template>
  <div class="overall-btns">
    <div
      v-for="item in floats"
      v-show="isVisible(item)"
      :key="item.key"
      class="side-menu-card font-weight-bold"
      :class="item.key"
      @click="onClick(item)"
    >
      <span>{{ item.label }}</span>
      <div :class="item.iconClass" />
    </div>
    <!-- 原项目 IM：TIM 未读 / 会话列表 / GetImToken 打开 -->
    <ImIframe />
    <!-- NEW：纯跳转 Contact TM\CS（VITE_SHOW_CONTACT_TM_NEW） -->
    <div
      v-if="showContactTmNew"
      class="side-menu-card contact contact-new"
      @click="openContactNew"
    >
      <span class="new-badge">NEW</span>
      <span>Contact TM\CS</span>
      <img class="contact-icon" :src="contactIcon" alt="" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.overall-btns {
  left: 0;
  bottom: 70px;
  position: fixed;
  z-index: 200;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.side-menu-card {
  z-index: 99;
  width: 37px;
  height: 32px;
  line-height: 32px;
  cursor: pointer;
  margin-bottom: 10px;
  position: relative;
  box-sizing: border-box;
  border-radius: 0 30px 30px 0;
  transition: all 0.5s;
  justify-content: space-between;
  display: flex;
  align-items: center;
  padding-right: 3px;

  &.feedback {
    border: 1px solid #f69898;
    background: #fef4f4;
    .feedback-menu {
      background: url("@/assets/img/common/sidemenu/feedback.png") no-repeat center;
      width: 26px;
      height: 26px;
    }
  }

  &.class {
    background: #f8effe;
    border: 1px solid #b866f8;
    .class-menu {
      background: url("@/assets/img/common/sidemenu/class.png") no-repeat center;
      width: 26px;
      height: 26px;
    }
  }

  &.moodle {
    background: #fff7eb;
    border: 1px solid #ffac3e;
    .moodle-menu {
      background: url("@/assets/img/common/sidemenu/moodle.png") no-repeat center;
      width: 26px;
      height: 26px;
    }
  }

  &.public {
    background: #f4fbf2;
    border: 1px solid #98db7c;
    .public-menu {
      background: url("@/assets/img/common/sidemenu/public.png") no-repeat center;
      width: 26px;
      height: 26px;
    }
  }

  &.contact {
    background: #e5fef3;
    border: 1px solid #1dc15a;

    .contact-icon {
      width: 26px;
      height: 26px;
      padding: 5px;
      box-sizing: border-box;
      display: block;
      flex-shrink: 0;
      border-radius: 30px;
    }
  }

  &.contact-new {
    .new-badge {
      position: absolute;
      top: -6px;
      right: -4px;
      z-index: 3;
      width: auto !important;
      height: 14px !important;
      line-height: 14px !important;
      padding: 0 4px;
      opacity: 1 !important;
      font-size: 8px;
      color: #fff;
      background: #ff3838;
      border-radius: 8px;
      white-space: nowrap;
      pointer-events: none;
    }
  }

  span {
    width: 0;
    overflow: hidden;
    height: 32px;
    opacity: 0;
    transition: all 0.5s;
    color: $blue-title;
    font-size: 12px;
    white-space: nowrap;
  }

  &:hover {
    width: 160px;
    span:not(.new-badge) {
      opacity: 1;
      width: auto;
      padding-left: 10px;
    }
    .feedback-menu {
      background: url("@/assets/img/common/sidemenu/feedback.png") no-repeat center
        rgba(246, 152, 152, 0.2);
      border-radius: 30px;
    }
    .class-menu {
      background: url("@/assets/img/common/sidemenu/class.png") no-repeat center
        rgba(184, 102, 248, 0.2);
      border-radius: 30px;
    }
    .moodle-menu {
      background: url("@/assets/img/common/sidemenu/moodle.png") no-repeat center
        rgba(255, 172, 62, 0.2);
      border-radius: 30px;
    }
    .public-menu {
      background: url("@/assets/img/common/sidemenu/public.png") no-repeat center
        rgba(152, 219, 124, 0.2);
      border-radius: 30px;
    }
    .contact-icon {
      background: rgba(29, 193, 90, 0.15);
    }
  }

  &.class:hover,
  &.moodle:hover,
  &.contact:hover {
    width: 180px;
  }
}

.font-weight-bold {
  font-weight: 700;
}
</style>
